/**
 * Gerenciador de cancelamento de requisições HTTP
 * Resolve race conditions em listas quando filtros mudam rapidamente
 *
 * @example
 * // Usuário filtra por Supervisor A, depois rapidamente por Supervisor B
 * // A requisição de Supervisor A é cancelada automaticamente
 */
class RequestCancellationManager {
  // Mapa de controllers ativos: chave → AbortController
  private controllers: Map<string, AbortController> = new Map();

  // Whitelist de recursos que DEVEM ter cancelamento automático
  private whitelist: Set<string> = new Set([
    'job-master-sheet',
    'bump-resource-sheet',
    'job-pending-resource',
    'prod-pay-master-sheet',
    'repair-receivables-sheet',
    'repair-payables-sheet',
  ]);

  /**
   * Obtém um AbortSignal para uma requisição
   * Cancela TODAS as requisições anteriores do mesmo recurso
   *
   * @param key - Chave única (resource + filtros + ordenação)
   * @param resource - Nome do recurso (ex: 'job-master-sheet')
   * @returns AbortSignal para passar ao axios
   */
  getSignal(key: string, resource: string): AbortSignal {
    // Verificar se o recurso está na whitelist
    if (!this.whitelist.has(resource)) {
      const newController = new AbortController();
      this.controllers.set(key, newController);
      return newController.signal;
    }

    // Cancelar TODAS as requisições do mesmo recurso (não só mesma chave)
    for (const [existingKey, controller] of this.controllers.entries()) {
      // Se a key começa com o mesmo resource, cancelar
      if (existingKey.startsWith(`${resource}_`)) {
        controller.abort();
        this.controllers.delete(existingKey);
      }
    }

    // Cria e armazena novo controller
    const newController = new AbortController();
    this.controllers.set(key, newController);

    return newController.signal;
  }

  /**
   * Remove controller do mapa após conclusão
   * Previne memory leaks
   *
   * @param key - Chave do controller a remover
   */
  removeController(key: string): void {
    this.controllers.delete(key);
  }

  /**
   * Verifica se um erro é de cancelamento (não é erro real)
   *
   * @param error - Erro capturado no catch
   * @returns true se for cancelamento
   */
  isAbortError(error: any): boolean {
    return (
      error.name === 'AbortError' ||
      error.name === 'CanceledError' ||
      error.code === 'ERR_CANCELED'
    );
  }
}

// Singleton exportado
export const requestCanceller = new RequestCancellationManager();
